import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useWatch } from "react-hook-form";

import { find, times, compact } from "lodash";

import useTranslations from "../../../hooks/useTranslations";
import useSchedule from "../../../hooks/useSchedule";
import useDateFnsLocale from "../../../hooks/useDateFnsLocale";

import { FormStepProps } from "../../../screens/ReviewFormScreen";

import { DatePicker, Select, FormLabel } from "../../wombat-ui";
import { useSet } from "react-use";
import { ReviewFullFragment } from '../../../graphql/generated/definitions';

const ReviewSchedulingForm: React.FC<
  Omit<FormStepProps, "onNext" | "formState"> & {
    defaultValues?: ReviewFullFragment;
  }
> = ({ register, unregister, setValue, control, defaultValues }) => {
  const t = useTranslations();

  const { startDate, endDate } = useWatch({
    control,
    name: ["startDate", "endDate"],
  });

  useEffect(() => {
    if (endDate && startDate && new Date(startDate) > new Date(endDate))
      setValue("endDate", undefined);
  }, [startDate, endDate]);

  const scheduleItems = useSchedule({
    schedule: defaultValues?.schedule,
    hasEndDate: !!endDate,
  });
  const [scheduleType, setScheduleType] = useState(
    scheduleItems.scheduleType || "months"
  );

  const [dayOfWeek, setDayOfWeek] = useState(scheduleItems.dayOfWeek || "1");

  const [dayOfMonthOption, setDayOfMonthOption] = useState(
    scheduleItems.dayOfMonthOption || "custom"
  );
  const [hour] = useState(scheduleItems.hour || "9");
  const [minute] = useState(scheduleItems.minute || "0");

  const [months, { toggle: toggleMonth, has: hasMonth }] = useSet(
    new Set(scheduleItems.months || [(new Date().getMonth() + 1).toString()])
  );

  const [daysOfMonth, { toggle: toggleDayOfMonth, has: hasDayOfMonth }] =
    useSet(new Set(scheduleItems.daysOfMonth || []));

  useEffect(() => {
    setDayOfMonthOption("custom");
  }, []);

  useEffect(() => {
    const start = startDate ? new Date(startDate) : startDate;

    if (start && !defaultValues?.schedule) {
      setDayOfWeek(start.getDay().toString());
      toggleDayOfMonth(start.getDate().toString());
    }
  }, [startDate]);

  const scheduleTypes = useMemo(
    () => [
      { value: "weeks", label: t("templates.scheduleType.weeks") },
      { value: "months", label: t("templates.scheduleType.months") },
      { value: "someMonths", label: t("templates.scheduleType.someMonths") },
      { value: "onetime", label: t("templates.scheduleType.onetime") },
      {
        value: "startAndFinish",
        label: t("templates.scheduleType.startAndFinish"),
      },
    ],
    []
  );

  const dayOfMonthOptions = useMemo(
    () => [
      { value: "first", label: t("templates.dayOfMonthOptions.first") },
      { value: "last", label: t("templates.dayOfMonthOptions.last") },
      { value: "custom", label: t("templates.dayOfMonthOptions.custom") },
    ],
    []
  );

  const dateFnsLocale = useDateFnsLocale();

  const handleChangeDayOfMonthOption = useCallback(
    ({ value }) => {
      setDayOfMonthOption(value);
    },
    [setDayOfMonthOption]
  );

  const defaultStartDate = useMemo(
    () =>
      defaultValues?.startDate ? new Date(defaultValues.startDate) : new Date(),
    [defaultValues]
  );

  const defaultEndDate = useMemo(
    () =>
      defaultValues?.endDate ? new Date(defaultValues.endDate) : undefined,
    [defaultValues]
  );

  const handleChangeScheduleType = useCallback(
    ({ value }) => {
      if (value === "onetime") {
        setValue("endDate", undefined);
      }
      setScheduleType(value);
    },
    [setScheduleType, setValue]
  );

  const schedule = useMemo(() => {
    switch (scheduleType) {
      case "weeks":
        return `${minute} ${hour} * * ${dayOfWeek}`;

      case "months":
      case "someMonths": {
        let monthsSelection: string;
        if (scheduleType == "months") {
          monthsSelection = "*";
        } else {
          monthsSelection = compact(Array.from(months)).join(",");
        }

        switch (dayOfMonthOption) {
          case "first":
            return `${minute} ${hour} 1 ${monthsSelection} *`;
          case "last":
            return `${minute} ${hour} L ${monthsSelection} *`;
          case "custom":
            return `${minute} ${hour} ${compact(Array.from(daysOfMonth)).join(
              ","
            )} ${monthsSelection} *`;
          default:
            return null;
        }
      }
      case "onetime":
        return null;

      default:
        return null;
    }
  }, [
    minute,
    hour,
    dayOfWeek,
    daysOfMonth,
    dayOfMonthOption,
    scheduleType,
    startDate,
    months,
  ]);

  useEffect(() => setValue("schedule", schedule), [schedule]);

  return (
    <>
      <input type="hidden" name="schedule" ref={register()} />
      <div className="flex">
        <Select
          items={scheduleTypes}
          id="scheduleTypes"
          name="scheduleTypes"
          label="Report schedule type"
          srOnlyLabel
          defaultValue={find(scheduleTypes, { value: scheduleType })}
          renderItem={(i) => i.label}
          getValue={(i) => i.value}
          onChange={handleChangeScheduleType}
        />
      </div>

      <DatePicker
        name="startDate"
        label={
          scheduleType === "onetime"
            ? (t("reviews.startDateOneTimeLabel") as string)
            : scheduleType === "startAndFinish"
            ? (t("reviews.startDateStartAndFinishLabel") as string)
            : (t("reviews.startDateScheduledLabel") as string)
        }
        defaultValue={defaultStartDate}
        RHF={{
          register,
          name: "startDate",
          unregister,
          setValue,
          registerOptions: {
            required: true,
          },
        }}
      />
      {startDate && (
        <>
          {scheduleType === "weeks" && (
            <FormLabel
              label={t("templates.dayOfWeekField")}
              className="inline-block pointer-events-none"
            >
              <div className="flex flex-row items-center space-x-1 text-sm">
                {["1", "2", "3", "4", "5", "6", "0"].map((day) => (
                  <button
                    key={day}
                    type="button"
                    className={`flex items-center justify-center w-8 h-8 text-xs rounded-full pointer-events-auto
                            ${
                              dayOfWeek === day
                                ? "bg-primary-500 text-light-100"
                                : "bg-light-100 text-light-900"
                            }
                            `}
                    onClick={() => setDayOfWeek(day)}
                  >
                    {dateFnsLocale.localize.day(day, { width: "narrow" })}
                  </button>
                ))}
              </div>
            </FormLabel>
          )}
          {(scheduleType === "months" || scheduleType === "someMonths") && (
            <>
              {scheduleType === "someMonths" && (
                <FormLabel
                  label={t("templates.monthsField")}
                  className="inline-block pointer-events-none"
                >
                  <div className="flex flex-row items-center space-x-1 text-sm">
                    {times(12, (month) => (month + 1).toString()).map(
                      (month) => (
                        <button
                          key={month}
                          type="button"
                          className={`flex items-center justify-center w-8 h-8 text-xs rounded-full pointer-events-auto
                                ${
                                  hasMonth(month)
                                    ? "bg-primary-500 text-light-100"
                                    : "bg-light-100 text-light-900"
                                }
                              `}
                          onClick={() => {
                            toggleMonth(month);
                          }}
                        >
                          {dateFnsLocale.localize.month(parseInt(month) - 1, {
                            width: "narrow",
                          })}
                        </button>
                      )
                    )}
                  </div>
                </FormLabel>
              )}
              <FormLabel
                label={t("templates.dayOfMonthField")}
                className="w-96"
              >
                <Select
                  items={dayOfMonthOptions}
                  id="dayOfMonthOption"
                  label="Day of the month"
                  srOnlyLabel
                  defaultValue={dayOfMonthOption}
                  renderItem={(i) => i.label}
                  getValue={(i) => i.value}
                  onChange={handleChangeDayOfMonthOption}
                />
                {dayOfMonthOption === "custom" && (
                  <div className="grid grid-cols-7 gap-3 pt-2 mt-4 text-sm w-96 auto-cols-min">
                    {times(31, (day) => (day + 1).toString()).map((day) => (
                      <button
                        key={day}
                        type="button"
                        className={`items-center justify-center w-8 h-8 text-xs rounded-full
                                  ${
                                    hasDayOfMonth(day)
                                      ? "bg-primary-500 text-light-100"
                                      : `${
                                          Number(day) <= 28
                                            ? "bg-light-100"
                                            : "bg-warning-100"
                                        } text-light-900`
                                  }
                                  `}
                        onClick={() => toggleDayOfMonth(day)}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                )}
              </FormLabel>
            </>
          )}
          {scheduleType !== "onetime" && (
            <FormLabel
              label={
                scheduleType === "startAndFinish"
                  ? t("reviews.endReportLabel")
                  : t("reviews.endDateLabel")
              }
            >
              <div className="flex">
                <DatePicker
                  name="endDate"
                  minDate={startDate ? new Date(startDate) : undefined}
                  RHF={{
                    register,
                    name: "endDate",
                    unregister,
                    setValue,
                  }}
                  defaultValue={defaultEndDate}
                />
              </div>
            </FormLabel>
          )}
        </>
      )}
    </>
  );
};

export default React.memo(ReviewSchedulingForm);
